// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import styles from './note-card.module.css'
import OwnerHint from './OwnerHint'
import { OWN_VIEWS_HINT } from 'lib/owner-notes/hints'
import {
  readersLabel,
  VIEWS_STARTING,
  viewsToGoLine,
  viewsWeekLine,
  type ViewsSummary,
} from 'lib/owner-notes/views'

interface ViewsRowProps {
  summary: ViewsSummary
}

/**
 * The author's readers row on a live own note (#138): the total, the last week, a daily
 * sparkline and the distance to the next milestone DealHunt announces. Under the first
 * milestone the row keeps its place and says readers are starting to find it — a small figure
 * would only discourage. The public card carries the total alone, for signed-in readers
 * (#155) — the week, the sparkline and the milestone stay the author's.
 */
const ViewsRow: React.VFC<ViewsRowProps> = ({ summary }) => {
  if (!summary.shown) {
    return (
      <div className={styles.views}>
        <OwnerHint hint={OWN_VIEWS_HINT}>
          <span className={styles.viewsStarting}>{VIEWS_STARTING}</span>
        </OwnerHint>
      </div>
    )
  }
  const max = Math.max(1, ...summary.days)
  const last = summary.days.length - 1
  const toGo = viewsToGoLine(summary)
  return (
    <div className={styles.views}>
      <div className={styles.viewsFigure}>
        <OwnerHint hint={OWN_VIEWS_HINT}>
          <span className={styles.viewsNum}>{summary.total.toLocaleString('en-US')}</span>
        </OwnerHint>
        <span className={styles.viewsUnit}>{summary.total === 1 ? 'reader' : 'readers'}</span>
      </div>
      <div className={styles.viewsSide}>
        <p className={styles.viewsLine}>{viewsWeekLine(summary)}</p>
        {summary.days.length > 0 && (
          <div
            className={styles.viewsSpark}
            role="img"
            aria-label={`Readers per day, last ${summary.days.length} days`}
          >
            {summary.days.map((v, i) => (
              <i
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={clsx(styles.viewsDay, { [styles.viewsToday]: i === last })}
                style={{ height: `${Math.max(8, Math.round((v / max) * 100))}%` }}
                title={readersLabel(v)}
              />
            ))}
          </div>
        )}
        <div
          className={styles.viewsBar}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={summary.progress}
          aria-label={toGo}
        >
          <b style={{ width: `${summary.progress}%` }} />
        </div>
        <p className={styles.fine}>{toGo}</p>
      </div>
    </div>
  )
}

export default ViewsRow
